// apps/web/src/lib/badges/badgeFacts.ts
// ─────────────────────────────────────────────────────────────────────────
// Builds a BadgeFacts snapshot from REAL app state for the pure evaluator.
//
// Honesty principle: retroactive facts come only from durable records the app
// already keeps. The richest, most trustworthy historical record is the
// reward ledger (nis.rewards.ledger) — every quiz pass, final test, module
// mastery, lesson read, and flashcard checkpoint is banked there with a
// stable id. We read those ids directly rather than re-deriving SRS state.
//
// AUDIT FIX: flashcard-based badges previously read the
// `lesson_flashcards_mastered:` prefix — an event that sits in DORMANT_EVENTS
// and never fires, so Locked In Memory / Fully Trained were unearnable. The
// live lesson-completion proof is `lesson_flashcards_checkpoint:` (fired when
// a lesson's Memory Anchors are solid), so all flashcard facts now read that.
//
// Forward-only signals live in their own durable marker store
// (badgeSignals.ts) so a fresh simulator run / Impact Lab run is remembered.
// ─────────────────────────────────────────────────────────────────────────

import { LEARNING_MODULES } from '@nisk/core/learning';
import type { BadgeFacts, ForwardSignals } from '@nisk/core/badges';
import { readForwardSignals } from './badgeSignals';
import { readDiscoveryResults } from '../discoveryStorage';

const LEDGER_KEY = 'nis.rewards.ledger';
// Must match lib/srs.ts STREAK_KEY (the only streak writer). Previously this
// read 'nis.streak', a key nothing wrote, so streak badges never fired.
const STREAK_KEY = 'nis.gamification.streak';
const FINANCIAL_KEY = 'nisk_financial';

// ── Ledger reading ────────────────────────────────────────────────────────
function readLedgerIds(): Set<string> {
	try {
		const raw = localStorage.getItem(LEDGER_KEY);
		if (raw) return new Set(JSON.parse(raw) as string[]);
	} catch {
		/* noop */
	}
	return new Set();
}

/** Count ledger ids matching a prefix, e.g. 'quiz_passed:'. */
function countPrefix(ids: Set<string>, prefix: string): number {
	let n = 0;
	for (const id of ids) if (id.startsWith(prefix)) n++;
	return n;
}

function hasPrefix(ids: Set<string>, prefix: string): boolean {
	for (const id of ids) if (id.startsWith(prefix)) return true;
	return false;
}

/** Distinct entity ids after a known prefix: 'prefix:entity[:rest]' → entity. */
function distinctEntities(ids: Set<string>, prefix: string): Set<string> {
	const out = new Set<string>();
	for (const id of ids) {
		if (!id.startsWith(prefix)) continue;
		const rest = id.slice(prefix.length);
		const entity = rest.split(':')[0];
		if (entity) out.add(entity);
	}
	return out;
}

// ── Curriculum lookups (school + tier mapping) ───────────────────────────

/** Map lessonId → moduleId for resolving lesson-keyed ledger ids. */
function buildLessonModuleMap(): Map<string, string> {
	const map = new Map<string, string>();
	for (const mod of LEARNING_MODULES) {
		for (const lesson of mod.lessons) map.set(lesson.id, mod.id);
	}
	return map;
}

/** Map lessonId → set of schools that lesson is tied to. */
function buildLessonSchoolMap(): Map<string, Set<string>> {
	const map = new Map<string, Set<string>>();
	for (const mod of LEARNING_MODULES) {
		for (const lesson of mod.lessons) {
			const schools = new Set<string>();
			if (mod.homeSchool) schools.add(mod.homeSchool);
			for (const s of lesson.schoolsOfThought ?? []) {
				if (s !== 'personal-finance-basics') schools.add(s);
			}
			map.set(lesson.id, schools);
		}
	}
	return map;
}

const TIER_MODULE_IDS: Map<string, string[]> = (() => {
	const map = new Map<string, string[]>();
	for (const mod of LEARNING_MODULES) {
		const list = map.get(mod.tier) ?? [];
		list.push(mod.id);
		map.set(mod.tier, list);
	}
	return map;
})();

// ── Streak / FI reads ─────────────────────────────────────────────────────
function readBestStreak(): number {
	try {
		const raw = localStorage.getItem(STREAK_KEY);
		if (!raw) return 0;
		const p = JSON.parse(raw) as { current?: number; longest?: number };
		return Math.max(p.current ?? 0, p.longest ?? 0);
	} catch {
		return 0;
	}
}

/**
 * Whether the user has a FI number worth crediting. We treat the presence of
 * a saved financial profile with the fields needed to compute FI (income +
 * expenses entered) as proof they've engaged the numbers. A freshly seeded
 * default profile is NOT credited — the forward signal handles the explicit
 * "calculated FI" action, and this retro check requires the user to have
 * actually saved their own inputs (mutated away from the seeded defaults).
 */
function readHasStoredFiNumber(): boolean {
	try {
		const raw = localStorage.getItem(FINANCIAL_KEY);
		if (!raw) return false;
		const p = JSON.parse(raw) as {
			incomeEntries?: { amount?: string }[];
			expenseEntries?: { amount?: string }[];
			_lastUpdated?: number;
		};
		const hasIncome = (p.incomeEntries ?? []).some(
			(e) => Number(e.amount) > 0,
		);
		const hasExpenses = (p.expenseEntries ?? []).some(
			(e) => Number(e.amount) > 0,
		);
		return hasIncome && hasExpenses;
	} catch {
		return false;
	}
}

// ── Discovery quiz reads ──────────────────────────────────────────────────
/** Count of distinct Discovery quizzes with a stored (completed) result. */
function readDiscoveryCompletedCount(): number {
	try {
		const map = readDiscoveryResults();
		let n = 0;
		for (const key of Object.keys(map)) {
			const stored = map[key as keyof typeof map];
			if (stored && stored.v === 1) n++;
		}
		return n;
	} catch {
		return 0;
	}
}

// ── "Learning actions in one day" heuristic ──────────────────────────────
// We can't fully reconstruct per-day action timestamps from the ledger
// (ids are not timestamped for one-shot events). Instead First Session is
// credited the moment the user has completed ANY two distinct meaningful
// learning actions overall, AND the streak shows at least one active day.
// This is an honest floor: two real actions = a real session.
function countMeaningfulActions(ids: Set<string>): number {
	let n = 0;
	n += countPrefix(ids, 'lesson_read:');
	n += countPrefix(ids, 'quiz_passed:');
	n += countPrefix(ids, 'quiz_first_try_passed:');
	n += countPrefix(ids, 'final_test_passed:');
	n += countPrefix(ids, 'lesson_flashcards_checkpoint:');
	return n;
}

export interface FactsInputs {
	/** Current level from the xp slice. */
	level: number;
}

/** Build the full BadgeFacts snapshot. */
export function gatherBadgeFacts(inputs: FactsInputs): BadgeFacts {
	const ids = readLedgerIds();

	const lessonModuleMap = buildLessonModuleMap();
	const lessonSchoolMap = buildLessonSchoolMap();

	// Lessons completed = distinct lesson_read ids.
	const lessonsRead = distinctEntities(ids, 'lesson_read:');
	const lessonsCompletedCount = lessonsRead.size;

	// Quizzes passed (checkpoints + first-try + finals all count as "a quiz").
	const quizzesPassedCount =
		distinctEntities(ids, 'quiz_passed:').size +
		distinctEntities(ids, 'quiz_first_try_passed:').size;

	const passedAnyQuizFirstTry = hasPrefix(ids, 'quiz_first_try_passed:');
	const passedAnyFinalTest = hasPrefix(ids, 'final_test_passed:');
	const acedAnyQuiz = hasPrefix(ids, 'quiz_aced:');
	// Live checkpoint event (see AUDIT FIX header note).
	const masteredAnyLessonFlashcards = hasPrefix(
		ids,
		'lesson_flashcards_checkpoint:',
	);

	// Fully trained lesson = read AND flashcard checkpoint AND (its module
	// has a passed quiz). We approximate per-lesson: a lesson is "fully
	// trained" when it's been read, its Memory Anchors checkpoint is done,
	// and its module has any quiz pass.
	const flashcardCheckpointLessons = distinctEntities(
		ids,
		'lesson_flashcards_checkpoint:',
	);
	const modulesWithQuizPass = new Set<string>([
		...distinctEntities(ids, 'quiz_passed:'),
		...distinctEntities(ids, 'quiz_first_try_passed:'),
	]);
	let fullyMasteredAnyLesson = false;
	for (const lessonId of flashcardCheckpointLessons) {
		if (!lessonsRead.has(lessonId)) continue;
		const modId = lessonModuleMap.get(lessonId);
		if (modId && modulesWithQuizPass.has(modId)) {
			fullyMasteredAnyLesson = true;
			break;
		}
	}

	// Modules mastered = distinct module_mastered ids.
	const masteredModuleIds = distinctEntities(ids, 'module_mastered:');
	const modulesMasteredCount = masteredModuleIds.size;

	// Tiers completed = every module in the tier is in masteredModuleIds.
	const tiersCompleted: string[] = [];
	for (const [tier, moduleIds] of TIER_MODULE_IDS) {
		if (
			moduleIds.length > 0 &&
			moduleIds.every((m) => masteredModuleIds.has(m))
		) {
			tiersCompleted.push(tier);
		}
	}

	// Worldviews: distinct schools across completed (read) lessons.
	const schoolsCompleted = new Set<string>();
	for (const lessonId of lessonsRead) {
		const schools = lessonSchoolMap.get(lessonId);
		if (schools) for (const s of schools) schoolsCompleted.add(s);
	}
	const completedAnySchoolLesson = schoolsCompleted.size >= 1;
	const distinctSchoolsCompletedCount = schoolsCompleted.size;

	// Exploration: exhibits, Discovery quizzes, Market Replay, Daily Review.
	const completedAnySchoolExhibit = hasPrefix(
		ids,
		'school_exhibit_completed:',
	);
	const discoveryQuizzesCompletedCount = readDiscoveryCompletedCount();
	const completedMarketReplay = hasPrefix(ids, 'market_replay_first:');
	// Date-scoped keys → each distinct date is one cleared Daily Review.
	const dailyReviewsClearedCount = distinctEntities(
		ids,
		'daily_review_cleared:',
	).size;

	const bestStreakDays = readBestStreak();

	// First Session floor (see note above).
	const meaningfulActions = countMeaningfulActions(ids);
	const maxLearningActionsInOneDay =
		meaningfulActions >= 2 && bestStreakDays >= 1 ? 2 : meaningfulActions;

	const forwardSignals: ForwardSignals = readForwardSignals();

	return {
		lessonsCompletedCount,
		quizzesPassedCount,
		passedAnyQuizFirstTry,
		passedAnyFinalTest,
		acedAnyQuiz,
		masteredAnyLessonFlashcards,
		fullyMasteredAnyLesson,
		modulesMasteredCount,
		tiersCompleted,
		maxLearningActionsInOneDay,
		completedAnySchoolLesson,
		distinctSchoolsCompletedCount,
		completedAnySchoolExhibit,
		discoveryQuizzesCompletedCount,
		completedMarketReplay,
		dailyReviewsClearedCount,
		bestStreakDays,
		level: inputs.level,
		hasStoredFiNumber: readHasStoredFiNumber(),
		forwardSignals,
	};
}
